import { z } from "zod";

import { DbEnum } from "../../../../generated/prisma-enums";
import type { DbEntity } from "../../../../generated/prisma-models";
import { JsonValueSchema } from "../../../../schemas/DeploymentSession/__json-value";

/**
 * The chat reads the chatroom routes return, as they cross the wire: the inbox and
 * playground pages, a chat by id, a chat with its members (also what
 * `POST /api/deployments/:id/message` answers with), and a search hit.
 *
 * Each schema mirrors one backend query exactly — `chatWithMembersInclude`,
 * `chatByIdInclude`, `chatListInclude` and `chatSearchSelect` in
 * `apps/backend/src/chats/classes/constants.ts` — and the backend asserts that
 * `z.infer` of each one is IDENTICAL to its Prisma payload type
 * (`apps/backend/src/chats/classes/chat-records-match-their-contracts.spec.ts`).
 *
 * The identity is what keeps these contracts safe to parse with: the generated
 * hook PARSES every response, and `z.object` strips a key it does not declare, so
 * a schema narrower than the query would silently delete a field the client
 * receives.
 *
 * Dates arrive as ISO strings over JSON and leave as `Date`; a `Json` column is
 * validated as a JSON value and typed as the column's own `DbEntity.JsonValue`.
 */

/** A `Json` column: validated as JSON, typed as the column Prisma returns. */
const jsonColumn = z.custom<DbEntity.JsonValue>(
  (value) => JsonValueSchema.safeParse(value).success,
  { message: "expected a JSON value" }
);

/** Every column of `Chat`, with each column's own nullability. */
const ChatRowShape = {
  id: z.string(),
  nanoId: z.string().nullable(),
  status: z.enum(DbEnum.CHAT_STATUS_VALUES),
  ticketStatus: z.enum(DbEnum.CHAT_TICKET_STATUS_VALUES),
  responseHandling: z.enum(DbEnum.CHAT_RESPONSE_HANDLING_VALUES),
  type: z.enum(DbEnum.CHAT_TYPE_VALUES),
  organizationId: z.string().nullable(),
  topic: z.string().nullable(),
  agentId: z.string().nullable(),
  agentIdSnapshot: z.string().nullable(),
  closedAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  unread: z.boolean(),
  metadata: jsonColumn,
  lookupTag: z.string().nullable(),
  settings: jsonColumn,
  deploymentSessionId: z.string().nullable(),
  comments: z.array(jsonColumn),
  numberOfPendingMessages: z.number().nullable(),
  lastMessageUpdatedAt: z.coerce.date().nullable(),
  lastMessageId: z.string().nullable(),
  currentMetaConversationId: z.string().nullable(),
  deploymentId: z.string().nullable(),
  deploymentAttribution: z.enum(DbEnum.DEPLOYMENT_ATTRIBUTION_VALUES),
  isEmulated: z.boolean(),
  parentChatId: z.string().nullable(),
  branchedAt: z.coerce.date().nullable(),
  threadRootTs: z.string().nullable()
};

const LastMessageSchema = z
  .object({
    createdAt: z.coerce.date(),
    content: jsonColumn,
    type: z.enum(DbEnum.MESSAGE_CONTENT_TYPE_VALUES).nullable(),
    status: z.enum(DbEnum.MESSAGE_STATUS_VALUES).nullable()
  })
  .nullable();

/** `customerVariableProjection` — the customer a prompt variable resolves against. */
const CustomerVariableShape = {
  customerIdentityId: z.string().nullable(),
  customerIdentity: z
    .object({
      id: z.string(),
      identifier: z.string(),
      service: z.enum(DbEnum.DEPLOYMENT_TYPE_VALUES),
      customer: z.object({
        displayName: z.string().nullable(),
        primaryEmail: z.string().nullable(),
        primaryPhone: z.string().nullable(),
        externalUserId: z.string().nullable(),
        metadata: jsonColumn
      })
    })
    .nullable()
};

const SessionDeploymentSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.enum(DbEnum.DEPLOYMENT_TYPE_VALUES)
});

/** What `getChatWithMembers` returns — `chatWithMembersInclude`. */
export const ChatWithMembersRecordSchema = z.object({
  ...ChatRowShape,
  members: z.array(
    z.object({
      userId: z.string(),
      chatId: z.string(),
      role: z.enum(DbEnum.CHAT_MEMBER_ROLE_VALUES),
      createdAt: z.coerce.date(),
      updatedAt: z.coerce.date().nullable(),
      user: z.object({
        id: z.string(),
        email: z.string(),
        firstName: z.string().nullable(),
        lastName: z.string().nullable()
      })
    })
  ),
  deploymentSession: z
    .object({
      deploymentId: z.string().nullable(),
      customerId: z.string().nullable(),
      deployment: SessionDeploymentSchema.nullable(),
      ...CustomerVariableShape
    })
    .nullable(),
  lastMessage: LastMessageSchema,
  _count: z.object({ messages: z.number() })
});

export type ChatWithMembersRecordDto = z.infer<typeof ChatWithMembersRecordSchema>;

/** What `getChatById` returns — `chatByIdInclude`, plus `assignedUsers` on the inbox view. */
export const ChatByIdRecordSchema = z.object({
  ...ChatRowShape,
  members: z.array(z.object({ user: z.object({ id: z.string() }) })),
  deploymentSession: z
    .object({
      deploymentId: z.string().nullable(),
      customerId: z.string().nullable(),
      lookupTag: z.string().nullable(),
      serviceThreadId: z.string().nullable(),
      emailMetadata: jsonColumn,
      slackMetadata: jsonColumn,
      telegramMetadata: jsonColumn,
      whatsappMetadata: jsonColumn,
      twilioMetadata: jsonColumn,
      teamsMetadata: jsonColumn,
      deployment: SessionDeploymentSchema.nullable(),
      ...CustomerVariableShape
    })
    .nullable(),
  lastMessage: LastMessageSchema,
  codeInterpreterSession: z.object({ id: z.string() }).nullable(),
  agent: z
    .object({
      first_name: z.string(),
      last_name: z.string(),
      profile_picture: z.string(),
      role: z.string()
    })
    .nullable(),
  /** Selected by the inbox view only. */
  assignedUsers: z.array(z.object({ id: z.string() })).optional(),
  hasCodeInterpreterSession: z.boolean()
});

export type ChatByIdRecordDto = z.infer<typeof ChatByIdRecordSchema>;

/** One row of an inbox or playground page — `chatListInclude`. */
export const ChatListRecordSchema = z.object({
  ...ChatRowShape,
  lastMessage: LastMessageSchema,
  deploymentSession: z
    .object({
      id: z.string(),
      deploymentId: z.string().nullable(),
      customerId: z.string().nullable(),
      serviceThreadId: z.string().nullable(),
      whatsappMetadata: jsonColumn,
      deployment: SessionDeploymentSchema.nullable(),
      ...CustomerVariableShape
    })
    .nullable(),
  agent: z
    .object({
      first_name: z.string(),
      last_name: z.string(),
      profile_picture: z.string(),
      role: z.string()
    })
    .nullable(),
  hasCodeInterpreterSession: z.boolean()
});

export type ChatListRecordDto = z.infer<typeof ChatListRecordSchema>;

/** One search hit — `chatSearchSelect`, plus the snippet a message match carries. */
export const ChatSearchRecordSchema = z.object({
  id: z.string(),
  topic: z.string().nullable(),
  status: z.enum(DbEnum.CHAT_STATUS_VALUES),
  settings: jsonColumn,
  metadata: jsonColumn,
  agent: z.object({ first_name: z.string(), last_name: z.string() }).nullable(),
  deploymentSession: z
    .object({
      customerIdentity: z.object({ service: z.enum(DbEnum.DEPLOYMENT_TYPE_VALUES) }).nullable()
    })
    .nullable(),
  /** Matched message text with `<mark>` highlighting — message-search hits only. */
  matchedMessageSnippet: z.string().optional()
});

export type ChatSearchRecordDto = z.infer<typeof ChatSearchRecordSchema>;
